import { Icon, IconButton, Image, ImageFit, Link, TooltipDelay, TooltipHost } from '@fluentui/react';
import React, { useState } from 'react';
import './Application.scss';
import { ILocationApplication, LaunchFormSource, recordLaunch } from '../../../shared/api/applications/applications';
import { IApplicationOperations } from './Apps';

interface IApplicationProps {
  applicationOperations: IApplicationOperations;
  application: ILocationApplication;
  userProfiles?: string[];
  categoryId?: string;
  form?: LaunchFormSource | string;
}

const ToggleDescriptionButton = (props: { appName: string; toggleDescription: () => void }) => (
  <TooltipHost
    content="Toggle description"
    calloutProps={{
      gapSpace: 0
    }}
  >
    <IconButton
      className="AppDescriptionIcon"
      iconProps={{
        iconName: 'Info'
      }}
      onClick={props.toggleDescription}
    />
  </TooltipHost>
);

const AppDescription = (props: { application: ILocationApplication }) => (
  <div className="AppDescriptionContainer ms-slideDownIn20">
    <div className="AppDescription">{props.application.description}</div>
    {props.application.moreInformationUrl && (
      <div className="MoreInformationContainer">
        <Link href={props.application.moreInformationUrl}>More information</Link>
      </div>
    )}
  </div>
);

const RemoveAppFromFavouritesButton = (props: { appName: string; toggleBelongingnessToFavorites: () => void }) => (
  <TooltipHost
    delay={TooltipDelay.long}
    content="App exists in your launcher. Click to remove."
    calloutProps={{
      gapSpace: 0
    }}
  >
    <IconButton
      className="AppAddRemoveButton"
      iconProps={{
        iconName: 'CheckMark'
      }}
      onClick={props.toggleBelongingnessToFavorites}
    />
  </TooltipHost>
);

const AddAppToFavouritesButton = (props: {
  appName: string;
  applicationOperations: IApplicationOperations;
  toggleBelongingnessToFavorites: () => void;
}) => (
  <TooltipHost
    delay={TooltipDelay.long}
    content={
      props.applicationOperations.canAddToFavorites()
        ? 'Add this app to your app launcher'
        : 'Your app launcher is full. You will need to remove apps before you can add more.'
    }
    calloutProps={{
      gapSpace: 0
    }}
  >
    <IconButton
      className="AppAddRemoveButton"
      iconProps={{
        iconName: 'Add'
      }}
      onClick={props.toggleBelongingnessToFavorites}
      disabled={!props.applicationOperations.canAddToFavorites()}
    />
  </TooltipHost>
);

export const Application: React.FC<IApplicationProps> = (props) => {
  const { application, applicationOperations, categoryId, form } = props;
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(false);

  const toggleDescription = () => setIsDescriptionVisible((prev) => !prev);

  const toggleBelongingnessToFavorites = () => applicationOperations.onToggleBelongingnessToFavorites(application);

  const onAppClick = () => {
    void recordLaunch(application.appId, application.locationCode, categoryId, form ?? 'Category').catch(() => {});
  };

  return (
    <>
      <div className="AppItem" key={application.id}>
        <Link
          href={application.url}
          title={application.appName}
          onClick={onAppClick}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Image src={application.iconUrl} width={32} height={32} imageFit={ImageFit.cover} />
          <span className="AppName">{application.appName}</span>
        </Link>
        <Icon iconName="OpenInNewWindow" className="ExternalLinkIcon" />
        {application.description && (
          <ToggleDescriptionButton appName={application.appName} toggleDescription={toggleDescription} />
        )}
        {applicationOperations.isFavorite(application) && (
          <RemoveAppFromFavouritesButton
            appName={application.appName}
            toggleBelongingnessToFavorites={toggleBelongingnessToFavorites}
          />
        )}
        {!applicationOperations.isFavorite(application) && (
          <AddAppToFavouritesButton
            appName={application.appName}
            applicationOperations={applicationOperations}
            toggleBelongingnessToFavorites={toggleBelongingnessToFavorites}
          />
        )}
      </div>
      {isDescriptionVisible && <AppDescription application={application} />}
    </>
  );
};
